import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomIcon } from '../../../../UI';
import carrierService from '../../../../../services/carrierService';
import CarrierPeopleSection from './CarrierPeopleSection';
import PhotoUploader from '../../../PhotoUploader/PhotoUploader';
import Loading from '../../../../loading-animation';

const CarrierSelectionSection = ({ onCarrierAdded }) => {
  const { setValue, watch } = useFormContext();
  
  // Watch carrier data from form (for edit mode)
  const formCarrier = watch('carrier');
  const carrierPhotos = watch('carrierPhotos') || [];
  const status = watch('status');
  
  // State for search
  const [searchType, setSearchType] = useState('companyName'); // 'companyName', 'mcNumber', 'dotNumber'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // State for new carrier form
  const [showNewCarrierForm, setShowNewCarrierForm] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [isNewCarrier, setIsNewCarrier] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    carrierCompanyName: '',
    carrierMC: '',
    carrierDOT: '',
    carrierAddress: '',
    carrierCity: '',
    carrierState: '',
    carrierZipCode: '',
    carrierPhone: '',
    carrierEmail: '',
    equipment: [
      { type: '', sizes: [{ value: '', custom: '' }] }
    ],
    capabilities: [],
    certifications: []
  });

  const handleCarrierPhotosChange = (photos) => {
    setValue('carrierPhotos', photos, { shouldDirty: true, shouldValidate: false });
  };
  
  const getEmptyCarrierFormData = () => ({
    carrierCompanyName: '',
    carrierMC: '',
    carrierDOT: '',
    carrierAddress: '',
    carrierCity: '',
    carrierState: '',
    carrierZipCode: '',
    carrierPhone: '',
    carrierEmail: '',
    equipment: [createEmptyEquipmentGroup()],
    capabilities: [],
    certifications: []
  });

  const resetCarrierState = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowNewCarrierForm(false);
    setSelectedCarrier(null);
    setIsNewCarrier(false);
    setIsInitialized(false);
    setFormData(getEmptyCarrierFormData());
  };

  useEffect(() => {
    if (status === 'Listed') {
      resetCarrierState();
    }
  }, [status]);

  // Initialize from form data (for edit mode)
  useEffect(() => {
    if (!isInitialized && formCarrier && (formCarrier.companyName || formCarrier.mcNumber || formCarrier.dotNumber)) {
      // Form has carrier data - initialize selectedCarrier and formData
      const equipmentGroups = normalizeEquipmentGroups(getEquipmentFromCarrier(formCarrier));
      const equipmentPayload = buildEquipmentPayload(equipmentGroups);
      const equipmentMeta = deriveEquipmentMeta(equipmentPayload);

      const carrierFromForm = {
        id: formCarrier.id || null,
        companyName: formCarrier.companyName || '',
        dba: formCarrier.dba ?? '',
        people: Array.isArray(formCarrier.people) ? formCarrier.people : [],
        mcNumber: formCarrier.mcNumber || '',
        dotNumber: formCarrier.dotNumber || '',
        name: formCarrier.name || formCarrier.companyName || '',
        phoneNumber: formCarrier.phoneNumber || '',
        email: formCarrier.email || '',
        address: {
          address: formCarrier.address?.address || '',
          city: formCarrier.address?.city || '',
          state: formCarrier.address?.state || '',
          zipCode: formCarrier.address?.zipCode || ''
        },
        equipment: equipmentPayload,
        capabilities: formCarrier.capabilities || [],
        certifications: formCarrier.certifications || []
      };
      
      setSelectedCarrier(carrierFromForm);
      setIsNewCarrier(!formCarrier.id);
      
      // Update local form data
      setFormData({
        carrierCompanyName: carrierFromForm.companyName,
        carrierMC: carrierFromForm.mcNumber,
        carrierDOT: carrierFromForm.dotNumber,
        carrierAddress: carrierFromForm.address.address,
        carrierCity: carrierFromForm.address.city,
        carrierState: carrierFromForm.address.state,
        carrierZipCode: carrierFromForm.address.zipCode,
        carrierPhone: carrierFromForm.phoneNumber,
        carrierEmail: carrierFromForm.email,
        equipment: equipmentGroups,
        capabilities: carrierFromForm.capabilities,
        certifications: carrierFromForm.certifications
      });
      
      // Also set values in react-hook-form
      setValue('carrier.id', carrierFromForm.id, { shouldDirty: false });
      setValue('carrier.people', carrierFromForm.people, { shouldDirty: false });
      setValue('carrier.companyName', carrierFromForm.companyName, { shouldDirty: false });
      setValue('carrier.dba', carrierFromForm.dba ?? '', { shouldDirty: false });
      setValue('carrier.mcNumber', carrierFromForm.mcNumber, { shouldDirty: false });
      setValue('carrier.dotNumber', carrierFromForm.dotNumber, { shouldDirty: false });
      setValue('carrier.name', carrierFromForm.name, { shouldDirty: false });
      setValue('carrier.phoneNumber', carrierFromForm.phoneNumber, { shouldDirty: false });
      setValue('carrier.email', carrierFromForm.email, { shouldDirty: false });
      setValue('carrier.address.address', carrierFromForm.address.address, { shouldDirty: false });
      setValue('carrier.address.city', carrierFromForm.address.city, { shouldDirty: false });
      setValue('carrier.address.state', carrierFromForm.address.state, { shouldDirty: false });
      setValue('carrier.address.zipCode', carrierFromForm.address.zipCode, { shouldDirty: false });
      setValue('carrier.equipment', equipmentPayload, { shouldDirty: false });
      setValue('carrier.equipmentType', equipmentMeta.equipmentType, { shouldDirty: false });
      setValue('carrier.size', equipmentMeta.size, { shouldDirty: false });
      setValue('carrier.capabilities', carrierFromForm.capabilities, { shouldDirty: false });
      setValue('carrier.certifications', carrierFromForm.certifications, { shouldDirty: false });
      
      setIsInitialized(true);
    }
  }, [formCarrier, isInitialized]);

  // Equipment Type options
  const equipmentTypeOptions = [
    'Auto Carrier',
    'Car Hauler/Enclosed Trailer',
    'Pickups',
    'Semi Truck',
    'Dry Van',
    'Reefer',
    'Box Truck',
    'Flatbed',
    'Step Deck',
    'Hot Shot',
    'Conestoga',
    'Curtain Van',
    'Sprinter Van',
    'Heavy Haul',
    'RGN',
    'Extendable RGN',
    'Lowboy',
    'Tilt Bed',
    'Landoll',
    'Power Only',
    'Tanker Endorsement',
    'Hopper Bottom',
    'Perimeter',
    'Flat Deck',
    'Dual Lane Transporter',
    'Super Dual Lane Transporter',
    'Drayage/Intermodal'
  ];

  // Size options
  const sizeOptions = [
    "16'",
    "24'",
    "26'",
    "40'",
    "48'",
    "53'",
    'N/A',
    'Other'
  ];

  const createEmptyEquipmentGroup = () => ({
    type: '',
    sizes: [{ value: '', custom: '' }]
  });

  const normalizeEquipmentGroups = (equipment = []) => {
    if (!Array.isArray(equipment) || equipment.length === 0) {
      return [createEmptyEquipmentGroup()];
    }

    return equipment.map(item => {
      const sizes = Array.isArray(item?.sizes) && item.sizes.length > 0
        ? item.sizes.map(size => {
            const trimmed = String(size || '').trim();
            if (!trimmed) return { value: '', custom: '' };
            if (sizeOptions.includes(trimmed)) {
              return { value: trimmed, custom: '' };
            }
            return { value: 'Other', custom: trimmed };
          })
        : [{ value: '', custom: '' }];

      return {
        type: String(item?.type || '').trim(),
        sizes
      };
    });
  };

  const buildEquipmentPayload = (equipmentGroups = []) => {
    if (!Array.isArray(equipmentGroups)) return [];
    return equipmentGroups
      .map(group => {
        const type = String(group?.type || '').trim();
        const sizes = Array.isArray(group?.sizes)
          ? group.sizes
              .map(size => {
                const value = size?.value === 'Other' ? size?.custom : size?.value;
                return String(value || '').trim();
              })
              .filter(Boolean)
          : [];
        return type ? { type, sizes } : null;
      })
      .filter(Boolean);
  };

  const deriveEquipmentMeta = (equipmentPayload = []) => {
    const equipmentType = equipmentPayload.map(item => item.type);
    const size = Array.from(
      new Set(equipmentPayload.flatMap(item => item.sizes || []))
    );
    return { equipmentType, size };
  };

  const getEquipmentFromCarrier = (carrier) => {
    if (Array.isArray(carrier?.equipment) && carrier.equipment.length > 0) {
      return carrier.equipment;
    }

    const equipmentType = Array.isArray(carrier?.equipmentType)
      ? carrier.equipmentType
      : carrier?.equipmentType
        ? [carrier.equipmentType]
        : [];
    const size = Array.isArray(carrier?.size)
      ? carrier.size
      : carrier?.size
        ? [carrier.size]
        : [];

    return equipmentType.map(type => ({
      type,
      sizes: size
    }));
  };

  const formatEquipmentSummary = (equipment = []) => {
    if (!Array.isArray(equipment) || equipment.length === 0) return '';
    return equipment
      .map(item => {
        const count = Array.isArray(item.sizes) ? item.sizes.length : 0;
        const label = count === 1 ? 'unit' : 'units';
        return `${item.type} ${count} ${label}`;
      })
      .join(', ');
  };

  // Capabilities options
  const capabilitiesOptions = [
    'Lift Gate',
    'Winch',
    'Team Drivers',
    'Driver Assistance',
    'Residential pickup/delivery',
    'Inside pickup/delivery',
    'Appointment-based deliveries',
    'Cross-docking/transloading',
    'White-glove',
    'Final-mile',
    'Blind Shipments'
  ];

  // Certifications options
  const certificationsOptions = [
    'HAZMAT',
    'TWIC',
    'SmartWay Certified',
    'C-TPAT',
    'ISO Certified',
    'UIIA Member',
    'Bonded Carrier',
    'Minority-Owned',
    'Women-Owned',
    'Veteran-Owned',
    'Disability-Owned'
  ];

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle select change
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle checkbox change for multi-select fields
  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [field]: newValues
      };
    });
  };

  const handleAddEquipmentGroup = () => {
    setFormData(prev => ({
      ...prev,
      equipment: [...prev.equipment, createEmptyEquipmentGroup()]
    }));
  };

  const handleRemoveEquipmentGroup = (groupIndex) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, index) => index !== groupIndex)
    }));
  };

  const handleEquipmentTypeChange = (groupIndex, value) => {
    setFormData(prev => {
      const equipment = [...prev.equipment];
      equipment[groupIndex] = {
        ...equipment[groupIndex],
        type: value
      };
      return { ...prev, equipment };
    });
  };

  const handleAddSize = (groupIndex) => {
    setFormData(prev => {
      const equipment = [...prev.equipment];
      const group = equipment[groupIndex];
      equipment[groupIndex] = {
        ...group,
        sizes: [...(group.sizes || []), { value: '', custom: '' }]
      };
      return { ...prev, equipment };
    });
  };

  const handleRemoveSize = (groupIndex, sizeIndex) => {
    setFormData(prev => {
      const equipment = [...prev.equipment];
      const group = equipment[groupIndex];
      const sizes = (group.sizes || []).filter((_, index) => index !== sizeIndex);
      equipment[groupIndex] = {
        ...group,
        sizes: sizes.length > 0 ? sizes : [{ value: '', custom: '' }]
      };
      return { ...prev, equipment };
    });
  };

  const handleSizeChange = (groupIndex, sizeIndex, value) => {
    setFormData(prev => {
      const equipment = [...prev.equipment];
      const group = equipment[groupIndex];
      const sizes = [...(group.sizes || [])];
      sizes[sizeIndex] = {
        value,
        custom: value === 'Other' ? sizes[sizeIndex]?.custom || '' : ''
      };
      equipment[groupIndex] = { ...group, sizes };
      return { ...prev, equipment };
    });
  };

  const handleCustomSizeChange = (groupIndex, sizeIndex, value) => {
    setFormData(prev => {
      const equipment = [...prev.equipment];
      const group = equipment[groupIndex];
      const sizes = [...(group.sizes || [])];
      sizes[sizeIndex] = {
        ...sizes[sizeIndex],
        custom: value
      };
      equipment[groupIndex] = { ...group, sizes };
      return { ...prev, equipment };
    });
  };

  // Handle manual form field changes - update form values in real-time
  useEffect(() => {
    if (showNewCarrierForm || isNewCarrier) {
      const equipmentPayload = buildEquipmentPayload(formData.equipment);
      const equipmentMeta = deriveEquipmentMeta(equipmentPayload);
      // Update form values when user manually edits fields
      setValue('carrier.companyName', formData.carrierCompanyName, { shouldDirty: true });
      setValue('carrier.mcNumber', formData.carrierMC, { shouldDirty: true });
      setValue('carrier.dotNumber', formData.carrierDOT, { shouldDirty: true });
      setValue('carrier.address.address', formData.carrierAddress, { shouldDirty: true });
      setValue('carrier.address.city', formData.carrierCity, { shouldDirty: true });
      setValue('carrier.address.state', formData.carrierState, { shouldDirty: true });
      setValue('carrier.address.zipCode', formData.carrierZipCode, { shouldDirty: true });
      setValue('carrier.name', formData.carrierCompanyName, { shouldDirty: true }); // Use company name as name
      setValue('carrier.phoneNumber', formData.carrierPhone, { shouldDirty: true });
      setValue('carrier.email', formData.carrierEmail, { shouldDirty: true });
      setValue('carrier.equipment', equipmentPayload, { shouldDirty: true });
      setValue('carrier.equipmentType', equipmentMeta.equipmentType, { shouldDirty: true });
      setValue('carrier.size', equipmentMeta.size, { shouldDirty: true });
      setValue('carrier.capabilities', formData.capabilities, { shouldDirty: true });
      setValue('carrier.certifications', formData.certifications, { shouldDirty: true });
      // Mark as new carrier (no ID means it will be created on server)
      setValue('carrier.id', null, { shouldDirty: true });
    }
  }, [formData, showNewCarrierForm, isNewCarrier, setValue]);

  // Handle new carrier form submission (just save to form, will be created on server)
  const handleSaveCarrier = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate required fields
    if (!formData.carrierCompanyName || !formData.carrierMC || !formData.carrierDOT || 
        !formData.carrierCity || !formData.carrierState || !formData.carrierZipCode ||
        !formData.equipment[0] || !formData.equipment[0].type || formData.equipment[0].type.trim() === '') {
      return;
    }
    const equipmentPayload = buildEquipmentPayload(formData.equipment);
    const equipmentMeta = deriveEquipmentMeta(equipmentPayload);

    const newCarrier = {
      id: null, // No ID means it's a new carrier to be created on server
      companyName: formData.carrierCompanyName,
      people: [], // People will be added separately via "Add People" section
      mcNumber: formData.carrierMC,
      dotNumber: formData.carrierDOT,
      name: formData.carrierCompanyName,
      phoneNumber: formData.carrierPhone || '',
      email: formData.carrierEmail || '',
      address: {
        address: formData.carrierAddress || '',
        city: formData.carrierCity,
        state: formData.carrierState,
        zipCode: formData.carrierZipCode
      },
      equipment: equipmentPayload,
      equipmentType: equipmentMeta.equipmentType,
      size: equipmentMeta.size,
      capabilities: Array.isArray(formData.capabilities) ? formData.capabilities : [],
      certifications: Array.isArray(formData.certifications) ? formData.certifications : []
    };
    
    setSelectedCarrier(newCarrier);
    setIsNewCarrier(true);
    
    // Update form values
    setValue('carrier.id', null, { shouldDirty: true });
    setValue('carrier.people', newCarrier.people, { shouldDirty: true }); // Set carrier people
    setValue('carrier.companyName', newCarrier.companyName, { shouldDirty: true });
    setValue('carrier.mcNumber', newCarrier.mcNumber, { shouldDirty: true });
    setValue('carrier.dotNumber', newCarrier.dotNumber, { shouldDirty: true });
    setValue('carrier.name', newCarrier.name, { shouldDirty: true });
    setValue('carrier.phoneNumber', newCarrier.phoneNumber, { shouldDirty: true });
    setValue('carrier.email', newCarrier.email, { shouldDirty: true });
    setValue('carrier.address.address', newCarrier.address.address, { shouldDirty: true });
    setValue('carrier.address.city', newCarrier.address.city, { shouldDirty: true });
    setValue('carrier.address.state', newCarrier.address.state, { shouldDirty: true });
    setValue('carrier.address.zipCode', newCarrier.address.zipCode, { shouldDirty: true });
    setValue('carrier.equipment', newCarrier.equipment || [], { shouldDirty: true });
    setValue('carrier.equipmentType', newCarrier.equipmentType || [], { shouldDirty: true });
    setValue('carrier.size', newCarrier.size || [], { shouldDirty: true });
    setValue('carrier.capabilities', newCarrier.capabilities, { shouldDirty: true });
    setValue('carrier.certifications', newCarrier.certifications, { shouldDirty: true });
    
    if (onCarrierAdded) {
      onCarrierAdded(newCarrier);
    }
    
    setShowNewCarrierForm(false);
  };

  // Search carriers with debounce
  const searchCarriers = useCallback(async (query, type) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Build search filters object based on search type
      const searchFilters = {};
      
      if (type === 'companyName') {
        searchFilters.companyName = query.trim();
      } else if (type === 'mcNumber') {
        searchFilters.mcNumber = query.trim();
      } else if (type === 'dotNumber') {
        searchFilters.dotNumber = query.trim();
      } else {
        // Default: search all fields
        searchFilters.q = query.trim();
      }
      
      // Note: type filter removed - people.type is now used instead
      
      const response = await carrierService.searchCarriers(searchFilters);
      const carriers = response?.data?.data || response?.data || [];
      setSearchResults(carriers);
      setShowSearchResults(true);
    } catch (error) {
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchCarriers(searchQuery, searchType);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, searchCarriers]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value) {
      setShowSearchResults(false);
      setSelectedCarrier(null);
      setIsNewCarrier(false);
    }
  };

  // Handle search type change
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Handle carrier selection from search results
  const handleCarrierSelect = (carrier) => {
    // Normalize carrier data structure
    const equipmentGroups = normalizeEquipmentGroups(getEquipmentFromCarrier(carrier));
    const equipmentPayload = buildEquipmentPayload(equipmentGroups);
    const equipmentMeta = deriveEquipmentMeta(equipmentPayload);

    const normalizedCarrier = {
      id: carrier._id || carrier.id,
      companyName: carrier.companyName || '',
      dba: carrier.dba || '',
      people: Array.isArray(carrier.people) ? carrier.people : [],
      mcNumber: carrier.mcNumber || '',
      dotNumber: carrier.dotNumber || '',
      name: carrier.name || carrier.companyName || '',
      phoneNumber: carrier.phoneNumber || '',
      email: carrier.email || '',
      address: {
        address: carrier.address?.address || carrier.address || '',
        city: carrier.address?.city || carrier.city || '',
        state: carrier.address?.state || carrier.state || '',
        zipCode: carrier.address?.zipCode || carrier.zipCode || ''
      },
      equipment: equipmentPayload,
      capabilities: Array.isArray(carrier.capabilities) ? carrier.capabilities : [],
      certifications: Array.isArray(carrier.certifications) ? carrier.certifications : []
    };

    setSelectedCarrier(normalizedCarrier);
    setIsNewCarrier(false);
    setShowSearchResults(false);
    setSearchQuery('');

    const preserveType = (t) => (t != null && String(t).trim() !== '') ? String(t).trim() : 'driver';
    const loadCarrierPeople = (Array.isArray(normalizedCarrier.people) ? normalizedCarrier.people : []).map(p => ({
      type: preserveType(p.type),
      fullName: p.fullName || '',
      email: p.email || '',
      phoneNumber: p.phoneNumber || ''
    }));
    setValue('carrier.id', normalizedCarrier.id, { shouldDirty: true });
    setValue('carrier.people', loadCarrierPeople, { shouldDirty: true });
    setValue('carrier.companyName', normalizedCarrier.companyName, { shouldDirty: true });
    setValue('carrier.dba', normalizedCarrier.dba ?? '', { shouldDirty: true });
    setValue('carrier.mcNumber', normalizedCarrier.mcNumber, { shouldDirty: true });
    setValue('carrier.dotNumber', normalizedCarrier.dotNumber, { shouldDirty: true });
    setValue('carrier.name', normalizedCarrier.name, { shouldDirty: true });
    setValue('carrier.phoneNumber', normalizedCarrier.phoneNumber, { shouldDirty: true });
    setValue('carrier.email', normalizedCarrier.email, { shouldDirty: true });
    setValue('carrier.address.address', normalizedCarrier.address.address, { shouldDirty: true });
    setValue('carrier.address.city', normalizedCarrier.address.city, { shouldDirty: true });
    setValue('carrier.address.state', normalizedCarrier.address.state, { shouldDirty: true });
    setValue('carrier.address.zipCode', normalizedCarrier.address.zipCode, { shouldDirty: true });
    setValue('carrier.equipment', equipmentPayload, { shouldDirty: true });
    setValue('carrier.equipmentType', equipmentMeta.equipmentType, { shouldDirty: true });
    setValue('carrier.size', equipmentMeta.size, { shouldDirty: true });
    setValue('carrier.capabilities', normalizedCarrier.capabilities, { shouldDirty: true });
    setValue('carrier.certifications', normalizedCarrier.certifications, { shouldDirty: true });

    // Update form data for manual editing
    setFormData({
      carrierCompanyName: normalizedCarrier.companyName,
      carrierMC: normalizedCarrier.mcNumber,
      carrierDOT: normalizedCarrier.dotNumber,
      carrierAddress: normalizedCarrier.address.address,
      carrierCity: normalizedCarrier.address.city,
      carrierState: normalizedCarrier.address.state,
      carrierZipCode: normalizedCarrier.address.zipCode,
      carrierPhone: normalizedCarrier.phoneNumber,
      carrierEmail: normalizedCarrier.email,
      equipment: equipmentGroups,
      capabilities: normalizedCarrier.capabilities,
      certifications: normalizedCarrier.certifications
    });

    if (onCarrierAdded) {
      onCarrierAdded(normalizedCarrier);
    }
  };

  // Handle manual edit - mark as new carrier
  const handleManualEdit = () => {
    setIsNewCarrier(true);
    setSelectedCarrier(null);
    setValue('carrier.id', null, { shouldDirty: true });
  };

  return (
    <div className="carrier-selection-section">
      <h3 className="form-section-title">Carrier</h3>
      
      {/* Search Section */}
      <div className="carrier-search-section">
        <div className="search-controls">
          <select
            value={searchType}
            onChange={handleSearchTypeChange}
            className="search-type-select"
          >
            <option value="companyName">Company Name</option>
            <option value="mcNumber">MC#</option>
            <option value="dotNumber">DOT#</option>
          </select>
          <div className="search-input-wrapper">
            <input
              type={searchType === 'companyName' ? 'text' : 'number'}
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={
                searchType === 'companyName' 
                  ? 'Search by Company Name...' 
                  : searchType === 'mcNumber'
                  ? 'Search by MC#...'
                  : 'Search by DOT#...'
              }
              className="carrier-search-input"
            />
            {isSearching && <Loading size="small" inline />}
          </div>
        </div>

        {/* Search Results */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((carrier) => (
              <div
                key={carrier._id || carrier.id}
                className="search-result-item"
                onClick={() => handleCarrierSelect(carrier)}
              >
                <div className="result-info">
                  <strong>{carrier.companyName || 'N/A'}</strong>
                  <div className="result-details">
                    {carrier.mcNumber && <span>MC#: {carrier.mcNumber}</span>}
                    {carrier.dotNumber && <span>DOT#: {carrier.dotNumber}</span>}
                    {carrier.city && carrier.state && (
                      <span>{carrier.city}, {carrier.state}</span>
                    )}
                  </div>
                </div>
                <CustomIcon name="right" size={16} />
              </div>
            ))}
          </div>
        )}

        {showSearchResults && searchResults.length === 0 && searchQuery && !isSearching && (
          <div className="no-results">
            No carriers found. You can add a new carrier manually.
          </div>
        )}
      </div>

      {/* Selected Carrier Info */}
      {selectedCarrier && !showNewCarrierForm && (
        <div className="selected-carrier-info">
          <div className="selected-carrier-header">
            <h4>Selected Carrier:</h4>
            <button
              type="button"
              className="edit-carrier-btn"
              onClick={() => {
                setShowNewCarrierForm(true);
                handleManualEdit();
              }}
            >
              Edit
            </button>
          </div>
          <p><strong>Company:</strong> {selectedCarrier.companyName}</p>
          {selectedCarrier.mcNumber && <p><strong>MC#:</strong> {selectedCarrier.mcNumber}</p>}
          {selectedCarrier.dotNumber && <p><strong>DOT#:</strong> {selectedCarrier.dotNumber}</p>}
          {selectedCarrier.address && (
            <p><strong>Address:</strong> {
              [
                selectedCarrier.address.address,
                selectedCarrier.address.city,
                selectedCarrier.address.state,
                selectedCarrier.address.zipCode
              ].filter(Boolean).join(', ')
            }</p>
          )}
          {selectedCarrier.equipment && Array.isArray(selectedCarrier.equipment) && selectedCarrier.equipment.length > 0 && (
            <p>
              <strong>Equipment:</strong> {formatEquipmentSummary(selectedCarrier.equipment)}
            </p>
          )}
          {selectedCarrier.capabilities && selectedCarrier.capabilities.length > 0 && (
            <p>
              <strong>Capabilities:</strong> {selectedCarrier.capabilities.join(', ')}
            </p>
          )}
          {selectedCarrier.certifications && selectedCarrier.certifications.length > 0 && (
            <p>
              <strong>Certifications:</strong> {selectedCarrier.certifications.join(', ')}
            </p>
          )}
          {isNewCarrier && (
            <p className="new-carrier-badge">⚠️ New carrier - will be created on server</p>
          )}
        </div>
      )}
      
      <button
        type="button"
        className="add-carrier-btn"
        onClick={() => {
          setShowNewCarrierForm(!showNewCarrierForm);
          if (!showNewCarrierForm) {
            handleManualEdit();
          }
        }}
      >
        <CustomIcon name="addCarrier" size={16} />
        {showNewCarrierForm ? 'Cancel' : 'Add New Carrier'}
      </button>

      {showNewCarrierForm && (
        <div className="new-carrier-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="carrierCompanyName">Company Name *</label>
              <input
                type="text"
                id="carrierCompanyName"
                name="carrierCompanyName"
                value={formData.carrierCompanyName}
                onChange={handleInputChange}
                required
                placeholder="Text"
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierMC">MC# *</label>
              <input
                type="number"
                id="carrierMC"
                name="carrierMC"
                value={formData.carrierMC}
                onChange={handleInputChange}
                required
                placeholder="Number"
                onWheel={(e) => e.target.blur()}
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierDOT">DOT# *</label>
              <input
                type="number"
                placeholder="number"
                id="carrierDOT"
                name="carrierDOT"
                value={formData.carrierDOT}
                onChange={handleInputChange}
                required
                onWheel={(e) => e.target.blur()}
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierAddress">Address</label>
              <input
                type="text"
                id="carrierAddress"
                name="carrierAddress"
                value={formData.carrierAddress}
                onChange={handleInputChange}
                placeholder="Text"
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierCity">City *</label>
              <input
                type="text"
                id="carrierCity"
                name="carrierCity"
                value={formData.carrierCity}
                onChange={handleInputChange}
                required
                placeholder="Text"
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierState">State *</label>
              <input
                type="text"
                id="carrierState"
                name="carrierState"
                value={formData.carrierState}
                onChange={handleInputChange}
                required
                placeholder="Text"
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierZipCode">Zip Code *</label>
              <input
                type="text"
                id="carrierZipCode"
                name="carrierZipCode"
                value={formData.carrierZipCode}
                onChange={handleInputChange}
                required
                placeholder="Text"
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierPhone">Phone Number</label>
              <input
                type="text"
                id="carrierPhone"
                name="carrierPhone"
                value={formData.carrierPhone}
                onChange={handleInputChange}
                placeholder="Text"
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrierEmail">Email</label>
              <input
                type="email"
                id="carrierEmail"
                name="carrierEmail"
                value={formData.carrierEmail}
                onChange={handleInputChange}
                placeholder="Text"
              />
            </div>

            {/* Equipment Types with Sizes */}
            <div className="equipment-section">
              <div className="equipment-section-header">
                <span className="equipment-section-label">Equipment Type</span>
                <button
                  type="button"
                  className="add-equipment-type-btn"
                  onClick={handleAddEquipmentGroup}
                  aria-label="Add Another Equipment Type"
                >
                  <CustomIcon name="plus-01" size={16} type="svg" />
                </button>
              </div>
              <div className="equipment-items-list">
                {(formData.equipment || []).map((group, groupIndex) => (
                  <div key={`equipment-group-${groupIndex}`} className="equipment-group">
                    <div className="form-group equipment-type-field">
                      <div className="equipment-type-input-wrapper">
                        <select
                          id={`equipmentType-${groupIndex}`}
                          name={`equipmentType-${groupIndex}`}
                          className={groupIndex > 0 ? 'equipment-type-select equipment-type-select--secondary' : 'equipment-type-select'}
                          value={group.type}
                          onChange={(e) => handleEquipmentTypeChange(groupIndex, e.target.value)}
                          required={groupIndex === 0}
                        >
                          <option value="" disabled hidden>
                            Select Equipment Type {groupIndex === 0 && '*'}
                          </option>
                          {equipmentTypeOptions
                            .filter(type => !formData.equipment.some((item, idx) => idx !== groupIndex && item.type === type) || type === group.type)
                            .map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                        </select>
                        {groupIndex > 0 && (
                          <button
                            type="button"
                            className="remove-equipment-type-btn"
                            onClick={() => handleRemoveEquipmentGroup(groupIndex)}
                            aria-label="Remove Equipment Type"
                          >
                            <CustomIcon name="close-small" size={14} type="svg" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="equipment-section equipment-sizes">
                      <div className="equipment-items-list">
                        {(group.sizes || []).map((sizeItem, sizeIndex) => (
                          <div key={`size-${groupIndex}-${sizeIndex}`} className="equipment-item-row">
                            <div className="form-group equipment-type-field">
                              <div className="equipment-type-input-wrapper">
                                <select
                                  id={`size-${groupIndex}-${sizeIndex}`}
                                  name={`size-${groupIndex}-${sizeIndex}`}
                                  value={sizeItem.value}
                                  onChange={(e) => handleSizeChange(groupIndex, sizeIndex, e.target.value)}
                                >
                                  <option value="">Select Size</option>
                                  {sizeOptions.map((size) => (
                                    <option key={size} value={size}>
                                      {size}
                                    </option>
                                  ))}
                                </select>
                                {sizeItem.value === 'Other' && (
                                  <input
                                    type="text"
                                    placeholder="Custom size"
                                    value={sizeItem.custom}
                                    onChange={(e) => handleCustomSizeChange(groupIndex, sizeIndex, e.target.value)}
                                  />
                                )}
                                {sizeIndex === 0 ? (
                                  <button
                                    type="button"
                                    className="add-equipment-type-btn add-size-btn"
                                    onClick={() => handleAddSize(groupIndex)}
                                    aria-label="Add Another Size"
                                  >
                                    <CustomIcon name="plus-01" size={14} type="svg" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="remove-equipment-type-btn"
                                    onClick={() => handleRemoveSize(groupIndex, sizeIndex)}
                                    aria-label="Remove Size"
                                  >
                                    <CustomIcon name="close-small" size={14} type="svg" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                   
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Capabilities Section */}
          <div className="form-section">
            <label className="form-section-label">Capabilities (Optional)</label>
            <div className="checkbox-group">
              {capabilitiesOptions.map((capability) => (
                <label key={capability} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.capabilities.includes(capability)}
                    onChange={() => handleCheckboxChange('capabilities', capability)}
                  />
                  <span>{capability}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Certifications Section */}
          <div className="form-section">
            <label className="form-section-label">Certifications (Optional)</label>
            <div className="checkbox-group">
              {certificationsOptions.map((certification) => (
                <label key={certification} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.certifications.includes(certification)}
                    onChange={() => handleCheckboxChange('certifications', certification)}
                  />
                  <span>{certification}</span>
                </label>
              ))}
            </div>
          </div>

          <button 
            type="button" 
            className="submit-carrier-btn"
            onClick={handleSaveCarrier}
          >
            Save Carrier
          </button>
        </div>
      )}

      <PhotoUploader
        title="Carrier Photos"
        photos={carrierPhotos}
        onPhotosChange={handleCarrierPhotosChange}
        onDeletedKeysChange={(deletedKey) => {
          const currentDeleted = watch('deletedCarrierImages') || [];
          if (!currentDeleted.includes(deletedKey)) {
            setValue('deletedCarrierImages', [...currentDeleted, deletedKey], { shouldDirty: true });
          }
        }}
        maxFileSize={120}
        acceptedTypes="image/png,image/jpeg,image/jpg"
        showCount={true}
      />

      {/* Carrier People Section */}
      <CarrierPeopleSection />

    </div>
  );
};

export default CarrierSelectionSection;
